<template>
  <div>
    <h2>{{ $t('parameters.factorsDatabase') }}</h2>
    <div v-if="isLoading" class="spinner-border text-info" role="status"></div>
    <div v-else class="white-container-item">
      <div style="display: flex; justify-content: flex-start; align-items: center; width: 100%; margin-bottom: 1rem;">
        <label class="real-toggle flex left" style="gap:0rem;" for="archivedFactorsToggle">
          <input
            type="checkbox"
            class="toggle__input"
            id="archivedFactorsToggle"
            v-model="archivedFactorsUsed"
            @change="updateArchivedFactorsUsed"
          />
          <span class="toggle-track">
            <span class="toggle-indicator">
              <span class="checkMark">
                <svg viewBox="0 0 24 24" id="ghq-svg-check" role="presentation" aria-hidden="true">
                  <path d="M9.86 18a1 1 0 01-.73-.32l-4.86-5.17a1.001 1.001 0 011.46-1.37l4.12 4.39 8.41-9.2a1 1 0 111.48 1.34l-9.14 10a1 1 0 01-.73.33h-.01z"></path>
                </svg>
              </span>
            </span>
          </span>
          <p class="toggle-text">
            {{ archivedFactorsUsed ? $t('parameters.archivedFactorsUsed') : $t('parameters.archivedFactorsNotUsed') }}
          </p>
        </label>
      </div>
      <div class="row">
        <div class="databases-container">
          <div
            v-for="database in factorsDatabases"
            :key="database.id"
            class="database-item"
            :class="{ 'no-license': isEcoinvent(database.id) && !hasEcoinventLicense }"
          >
            <div class="db-content" :class="{ 'inactive': !isDatabaseActive(database.id) }">
              <input
                type="checkbox"
                :id="'db-' + database.id"
                :checked="isDatabaseActive(database.id)"
                @change="toggleDatabase(database.id, $event.target.checked)"
                class="db-checkbox"
              />
              <label :for="'db-' + database.id" class="db-label">
                {{ database.name }}
              </label>
            </div>
            <!-- Badge "pas de licence" pour Ecoinvent (Story 1.5) -->
            <span v-if="isEcoinvent(database.id) && !hasEcoinventLicense" class="no-license-badge">
              <font-awesome-icon :icon="['fas', 'lock']" />
              <span class="no-license-tooltip">{{ $t('parameters.ecoinventNoLicense') }}</span>
            </span>
            <toolType
              v-if="getDescription(database)"
              :innerHtml="getDescription(database)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import instance from '../../main';
import eventBus from '../../eventBus'
import toolType from './toolType.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const ECOINVENT_DB_ID = 9

export default {
  components: {
    toolType,
    FontAwesomeIcon
  },
  props: {
    propsCompany: Object
  },
  data() {
    return {
      isLoading: true,
      factorsDatabases: [],
      companyFactorsDatabases: [],
      archivedFactorsUsed: false
    }
  },
  async mounted() {
    await this.getFactorsDatabases()
    await this.getCompanyFactorsDatabases()
    this.archivedFactorsUsed = this.propsCompany?.archived_factors_used || false
    this.isLoading = false
  },
  computed: {
    // Story 1.5: Check if company has Ecoinvent license
    hasEcoinventLicense() {
      return this.propsCompany?.has_ecoinvent_license === true
    }
  },
  methods: {
    // Story 1.5: Check if database is Ecoinvent
    isEcoinvent(databaseId) {
      return databaseId === ECOINVENT_DB_ID
    },
    async getFactorsDatabases() {
      await instance.get(`/factors-databases`, {
        headers: {
          "token": localStorage.getItem('token'),
          "company": localStorage.getItem('companyId')
        }
      }).then(response => {
        this.factorsDatabases = response.data.data || []
      }).catch(error => {
        console.log("Error while getting factors databases")
        console.log(error)
      })
    },
    async getCompanyFactorsDatabases() {
      await instance.get(`/company-factors-databases`, {
        headers: {
          "token": localStorage.getItem('token'),
          "company": localStorage.getItem('companyId')
        }
      }).then(response => {
        this.companyFactorsDatabases = response.data.data || []
      }).catch(error => {
        console.log("Error while getting company factors databases")
        console.log(error)
      })
    },
    isDatabaseActive(databaseId) {
      const companyDb = this.companyFactorsDatabases.find(db => db.database_id === databaseId)
      return companyDb ? companyDb.active : false
    },
    async toggleDatabase(databaseId, active) {
      eventBus.emit('show-toast', {
        type: 'info',
        message: this.$t('toast.updating')
      })

      await instance.post(`/company-factors-databases`, {
        database_id: databaseId,
        active: active
      }, {
        headers: {
          "token": localStorage.getItem('token'),
          "company": localStorage.getItem('companyId')
        }
      }).then(() => {
        eventBus.emit('show-toast', {
          type: 'success',
          message: this.$t('toast.updateSuccess')
        })

        // Update local data
        const existingIndex = this.companyFactorsDatabases.findIndex(db => db.database_id === databaseId)
        if (existingIndex !== -1) {
          this.companyFactorsDatabases[existingIndex].active = active
        } else {
          this.companyFactorsDatabases.push({ database_id: databaseId, active: active })
        }
      }).catch(error => {
        eventBus.emit('show-toast', {
          type: 'error',
          message: this.$t('toast.updateError')
        })
        console.log("Error while updating company factors database")
        console.log(error)
      })
    },
    getDescription(database) {
      const locale = this.$i18n.locale
      return database[`description_${locale}`] || database.description_fr
    },
    async updateArchivedFactorsUsed() {
      eventBus.emit('show-toast', {
        type: 'info',
        message: this.$t('toast.updating')
      })

      await instance.put(`/companies/archived-factors`, {
        archived_factors_used: this.archivedFactorsUsed
      }, {
        headers: {
          "token": localStorage.getItem('token'),
          "company": localStorage.getItem('companyId')
        }
      }).then(() => {
        eventBus.emit('show-toast', {
          type: 'success',
          message: this.$t('toast.updateSuccess')
        })
        var currentCompany = this.propsCompany
        currentCompany.archived_factors_used = this.archivedFactorsUsed
        localStorage.setItem('currentCompany', JSON.stringify(currentCompany))

        var companies = JSON.parse(localStorage.getItem('companies'))
        companies = companies.map(c => {
          if(c.id == currentCompany.id){
            return currentCompany
          }
          return c
        })
        localStorage.setItem('companies', JSON.stringify(companies))

      }).catch(error => {
        eventBus.emit('show-toast', {
          type: 'error',
          message: this.$t('toast.updateError')
        })
        console.log("Error while updating archived factors used")
        console.log(error)
      })
    }
  }
}
</script>

<style scoped>
.databases-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
}

.database-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.db-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: opacity 0.2s;
}

.db-content.inactive {
  opacity: 0.4;
}

.db-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.db-label {
  cursor: pointer;
  margin: 0;
  font-weight: 500;
  white-space: nowrap;
}

/* ========================================
   ECOINVENT NO LICENSE (Story 1.5)
   ======================================== */

.database-item.no-license {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  margin: -0.5rem 0;
}

.no-license-badge {
  position: relative;
  color: #92400e;
  font-size: 14px;
  cursor: help;
}

.no-license-tooltip {
  display: none;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #1f2937;
  color: white;
  font-size: 13px;
  border-radius: 6px;
  width: 220px;
  white-space: normal;
  line-height: 1.4;
  text-align: center;
  z-index: 100;
}

.no-license-badge:hover .no-license-tooltip {
  display: block;
}
</style>
